import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import ChartOfAccountsService from '@/api/chartofaccounts.service';
import { AccountType, AccountSubtype } from '@/types';

const accountTypes: AccountType[] = ['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE'];

// Map each account type to its possible subtypes
const accountSubtypes: Record<AccountType, AccountSubtype[]> = {
  'ASSET': ['Current Asset', 'Non-Current Asset', 'Cash and Cash Equivalents', 'Accounts Receivable', 'Inventory', 'Fixed Assets'],
  'LIABILITY': ['Current Liability', 'Non-Current Liability', 'Accounts Payable', 'Loans', 'Taxes Payable'],
  'EQUITY': ['Owner Equity', 'Retained Earnings', 'Capital'],
  'INCOME': ['Sales', 'Other Income', 'Revenue', 'Discount'],
  'EXPENSE': ['Cost of Sales', 'Operating Expense', 'Other Expense', 'Salaries', 'Rent', 'Utilities', 'Advertising'],
};

export default function CreateChartOfAccount() {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState<AccountType | ''>('');
  const [subtype, setSubtype] = useState<string>('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isDefault, setIsDefault] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  const handleTypeChange = (value: string) => {
    setType(value as AccountType);
    setSubtype(''); // Reset subtype when type changes
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!code || !name || !type) {
      toast({
        title: 'Required Fields Missing',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }
    
    try {
      setIsSaving(true);
      
      const accountData = {
        code,
        name,
        type,
        subtype: subtype || undefined,
        description: description || undefined,
        isActive,
        defaultAccount: isDefault,
      };
      
      const response = await ChartOfAccountsService.createChartOfAccount(accountData, selectedBusiness.id);
      
      if (response.data.result === 'success') {
        toast({
          title: 'Account Created',
          description: 'Chart of account has been created successfully',
        });
        navigate('/chart-of-accounts');
      } else {
        toast({
          title: 'Error',
          description: response.data.message,
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error creating account:', error);
      toast({
        title: 'Error',
        description: 'An error occurred while creating the account',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="container mx-auto py-6">
      <div className="flex items-center mb-6">
        <Button
          onClick={() => navigate(-1)}
          variant="ghost"
          size="sm"
          className="mr-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <h1 className="text-2xl font-semibold">Create Chart of Account</h1>
      </div>
      
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Account Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="code">
                      Account Code <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="code"
                      placeholder="e.g., 1000"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      className="font-mono"
                      required
                    />
                    <p className="text-xs text-muted-foreground">
                      A unique code for this account (e.g., 1000 for Cash)
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="name">
                      Account Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="name"
                      placeholder="e.g., Cash"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                    <p className="text-xs text-muted-foreground">
                      Descriptive name for the account
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="type">
                      Account Type <span className="text-red-500">*</span>
                    </Label>
                    <Select value={type} onValueChange={handleTypeChange} required>
                      <SelectTrigger id="type">
                        <SelectValue placeholder="Select account type" />
                      </SelectTrigger>
                      <SelectContent>
                        {accountTypes.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      The primary classification of this account
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="subtype">Subtype</Label>
                    <Select 
                      value={subtype} 
                      onValueChange={setSubtype}
                      disabled={!type}
                    >
                      <SelectTrigger id="subtype">
                        <SelectValue placeholder="Select subtype (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {type && accountSubtypes[type]?.map((subtype) => (
                          <SelectItem key={subtype} value={subtype}>
                            {subtype}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Further classification within the account type
                    </p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Enter a description of the account purpose and usage"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
          
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Account Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="active">Active Status</Label>
                    <p className="text-sm text-muted-foreground">
                      Can be used for transactions
                    </p>
                  </div>
                  <Switch
                    id="active"
                    checked={isActive}
                    onCheckedChange={setIsActive}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="default">Default Account</Label>
                    <p className="text-sm text-muted-foreground">
                      Will be used as a default option
                    </p>
                  </div>
                  <Switch
                    id="default"
                    checked={isDefault}
                    onCheckedChange={setIsDefault}
                  />
                </div>
              </CardContent>
            </Card>
            
            <div className="flex justify-end">
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Create Account
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
} 